/**
 * Application wiring: one GameConnection, one MultiplierModel, the two stores.
 *
 * Kept apart from socket.ts so the transport stays testable without importing
 * the global stores.
 */
import { GameConnection } from "./socket.js";
import type { ServerEventName, ServerEventPayload } from "./eventSchemas.js";
import { MultiplierModel } from "../game/core/MultiplierModel.js";
import { useConnectionStore } from "../state/connectionStore.js";
import { useRoundStore } from "../state/roundStore.js";
import { dispatchChatEvent } from "../features/chat/chatEvents.js";
import { useChatStore } from "../features/chat/chatStore.js";

/** Shared by the Pixi ticker and the DOM text loop; never stored in React state. */
export const multiplierModel = new MultiplierModel();

let connection: GameConnection | null = null;

export function getConnection(): GameConnection {
  connection ??= new GameConnection({
    multiplier: multiplierModel,
    handlers: {
      onStatus: (status) => {
        if (status === "connecting" || status === "disconnected") useChatStore.getState().reset();
        useConnectionStore.getState().setStatus(status);
      },
      onClockHealth: (health) => {
        useConnectionStore.getState().setClockHealth(health);
      },
      onEvent: dispatchToStore,
    },
  });
  return connection;
}

/** Server time, or the local clock before the first sync. */
export function serverNow(): number {
  return getConnection().clock.now();
}

function dispatchToStore<K extends ServerEventName>(event: K, payload: ServerEventPayload<K>): void {
  dispatchChatEvent(event, payload);
  const store = useRoundStore.getState();
  switch (event) {
    case "state:sync":
      store.onStateSync(payload as ServerEventPayload<"state:sync">);
      break;
    case "round:betting":
      store.onRoundBetting(payload as ServerEventPayload<"round:betting">);
      break;
    case "round:start":
      store.onRoundStart(payload as ServerEventPayload<"round:start">);
      break;
    case "round:crash":
      store.onRoundCrash(payload as ServerEventPayload<"round:crash">);
      break;
    case "bets:batch":
      store.onBetsBatch(payload as ServerEventPayload<"bets:batch">);
      break;
    case "me:bet":
      store.onMyBet(payload as ServerEventPayload<"me:bet">);
      break;
    case "me:balance":
      store.onBalance(payload as ServerEventPayload<"me:balance">);
      break;
    case "players:online":
      store.onOnline(payload as ServerEventPayload<"players:online">);
      break;
    default:
      break;
  }
}
